import React from "react";
import { useParams, Navigate, useSearchParams } from "react-router-dom";
import { useCustomerContext } from "@/context/CustomerContext";
import { useAuth } from "@/context/AuthContext";
import ReturnChatbot from "@/pages/chat/ReturnChatbot";

/**
 * Wrapper page that loads order data from CustomerContext
 * OR directly from URL parameters for Guest (magic-link) users.
 * Accessed via /customer/return-chat/:orderId
 */
const ReturnChatPage: React.FC = () => {
    const { orderId } = useParams<{ orderId: string }>();
    const [searchParams] = useSearchParams();
    const productIdFromUrl = searchParams.get('productId');
    const { loginType } = useAuth();
    const { orders, isLoading } = useCustomerContext();

    const isGuest = loginType === 'token';
    const isRegular = loginType === 'regular';

    // Show spinner ONLY for regular users waiting for their INITIAL dashboard data (orders)
    // We check if orders is empty to avoid showing a full-page loader during background refreshes
    if (isLoading && isRegular && (!orders || orders.length === 0)) {
        return (
            <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-[#F2F2F2]">
                <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-[#00C5FB]" />
            </div>
        );
    }

    const order = orders?.find((o: any) => o.id === orderId || o.order_id === orderId);

    // If we are a Guest, we don't expect to find the order in context (dashboard bypassed)
    // We rely on orderId from params and productId from URL
    if (isGuest) {
        if (!orderId || !productIdFromUrl) {
            console.error("[ReturnChatPage] Missing guest parameters", { orderId, productIdFromUrl });
            return <Navigate to="/token-expired" replace />;
        }

        return (
            <ReturnChatbot
                merchantId=""
                orderId={orderId}
                productId={productIdFromUrl}
            />
        );
    }

    // Regular User Flow
    if (!order) {
        return <Navigate to="/customer/orders" replace />;
    }

    return (
        <ReturnChatbot
            merchantId=""
            orderId={order.order_id}
            productId={order.product_id}
        />
    );
};

export default ReturnChatPage;
